import React, { useEffect, useMemo, useState } from "react";
import { adminService } from "../../services/adminService";
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  PhoneIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import LogoLoader from "../Common/LogoLoader";

function agentName(agent) {
  const details = agent?.agentDetails || {};
  return details.agent_name || [agent?.firstName, agent?.lastName].filter(Boolean).join(" ") || agent?.username || "Agent";
}

function agentPlace(agent) {
  const details = agent?.agentDetails || {};
  return [details.streetAddress, details.localGovernmentAddress, agent?.state].filter(Boolean).join(", ");
}

const AdminReassignModal = ({ isOpen, onClose, binIds = [], currentAgentId, sellerLabel, sellerPlace, onSuccess }) => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedState, setSelectedState] = useState("all");
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!isOpen) return undefined;
    setSelectedAgentId(null);
    setSearchTerm("");
    setSelectedState("all");
    setMessage("");
    let active = true;
    setLoading(true);
    adminService
      .getAllAgents()
      .then((list) => {
        if (!active) return;
        setAgents((list || []).filter((agent) => agent.isAgent && agent.role === "agent"));
      })
      .catch(() => {
        if (active) setMessage("Could not load agents. Try again.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [isOpen]);

  const states = useMemo(
    () => [...new Set(agents.map((agent) => agent.state).filter(Boolean))].sort(),
    [agents]
  );

  const filteredAgents = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return agents.filter((agent) => {
      if (selectedState !== "all" && agent.state !== selectedState) return false;
      if (!term) return true;
      const details = agent.agentDetails || {};
      const haystack = [
        agentName(agent),
        agent.state,
        agent.phoneNumber,
        details.phone_number,
        details.localGovernmentAddress,
        details.streetAddress,
        agent.homeAddress,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [agents, searchTerm, selectedState]);

  const handleAssign = async () => {
    if (!selectedAgentId) {
      setMessage("Select the agent who should take these orders.");
      return;
    }
    setSaving(true);
    setMessage("");
    try {
      const result = await adminService.reassignPendingValidations({
        binIds,
        agentId: selectedAgentId,
      });
      const notes = [];
      if (result.data?.skipped) notes.push(`${result.data.skipped} already with this agent`);
      if (result.data?.unavailable) notes.push(`${result.data.unavailable} no longer pending`);
      setMessage([result.message || "Orders assigned.", notes.join(". ")].filter(Boolean).join(" "));
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 900);
    } catch (error) {
      setMessage(error.response?.data?.message || "Could not assign these orders.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;
  const count = binIds.length;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 py-8 text-center">
        <div className="fixed inset-0 bg-gray-500/75" onClick={saving ? undefined : onClose} />
        <div className="relative inline-block w-full max-w-4xl overflow-hidden rounded-lg bg-white text-left shadow-xl">
          <div className="flex items-center justify-between bg-green-600 px-6 py-4">
            <div>
              <h3 className="text-lg font-semibold text-white">
                {count > 1 ? `Assign ${count} orders` : "Change assigned agent"}
              </h3>
              <p className="text-sm text-green-50">
                {sellerLabel}
                {sellerPlace ? ` · ${sellerPlace}` : ""}
              </p>
            </div>
            <button type="button" onClick={onClose} className="text-white hover:text-green-100" disabled={saving}>
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="px-6 py-4">
            <div className="mb-4 space-y-3">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, address, state, or phone..."
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 focus:border-green-500 focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <label className="text-sm font-medium text-gray-700">Agent state</label>
                <select
                  value={selectedState}
                  onChange={(event) => setSelectedState(event.target.value)}
                  className="rounded-lg border border-gray-300 px-3 py-2 focus:border-green-500 focus:ring-2 focus:ring-green-500"
                >
                  <option value="all">All states</option>
                  {states.map((state) => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
                <span className="text-sm text-gray-600">{filteredAgents.length} agent(s)</span>
              </div>
            </div>

            {message && (
              <div className={`mb-4 rounded-lg p-3 text-sm ${message.toLowerCase().includes("assigned") || message.toLowerCase().includes("order") && !message.toLowerCase().includes("could") ? "bg-green-100 text-green-800" : "bg-red-100 text-red-700"}`}>
                {message}
              </div>
            )}

            {loading ? (
              <div className="py-12 text-center">
                <LogoLoader />
              </div>
            ) : filteredAgents.length === 0 ? (
              <p className="py-12 text-center text-gray-600">No approved agents match this search.</p>
            ) : (
              <div className="max-h-96 space-y-3 overflow-y-auto">
                {filteredAgents.map((agent) => {
                  const selected = selectedAgentId === agent._id;
                  const current = currentAgentId && String(currentAgentId) === String(agent._id);
                  const place = agentPlace(agent);
                  const phone = agent.agentDetails?.phone_number || agent.phoneNumber;
                  return (
                    <button
                      key={agent._id}
                      type="button"
                      onClick={() => setSelectedAgentId(agent._id)}
                      className={`w-full rounded-lg border-2 p-4 text-left transition ${
                        selected ? "border-green-600 bg-green-50" : "border-gray-200 hover:border-green-300"
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
                          <UserIcon className="h-7 w-7 text-gray-400" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <h4 className="font-semibold text-gray-900">{agentName(agent)}</h4>
                            <div className="flex gap-2">
                              {current && (
                                <span className="rounded bg-gray-200 px-2 py-1 text-xs font-medium text-gray-700">Current</span>
                              )}
                              {selected && (
                                <span className="rounded bg-green-600 px-2 py-1 text-xs font-medium text-white">Selected</span>
                              )}
                            </div>
                          </div>
                          {phone && (
                            <p className="mt-1 flex items-center text-sm text-gray-600">
                              <PhoneIcon className="mr-2 h-4 w-4" />
                              {phone}
                            </p>
                          )}
                          <p className="mt-1 flex items-start text-sm text-gray-600">
                            <MapPinIcon className="mr-2 mt-0.5 h-4 w-4 flex-shrink-0" />
                            <span>{place || "Address not set"}</span>
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 bg-gray-50 px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAssign}
              disabled={saving || !selectedAgentId}
              className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? "Assigning..." : count > 1 ? `Assign ${count} orders` : "Assign agent"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminReassignModal;
